import React from 'react';
import { 
  BlockReplacement as BlockReplacementType, 
  BlockBuilder,
  BlockElement
} from '../../types';
import BlockRenderer from '../../BlockRenderer';
import { useBlockKit } from '../../context/BlockKitContext';

/**
 * Interface for the BlockReplacement component props
 */
interface BlockReplacementProps {
  block: BlockReplacementType;
}

/**
 * BlockReplacement component renders dynamic content from replacements object
 * 
 * @param {BlockReplacementProps} props - Component props
 * @returns {React.ReactElement | null} Rendered replacement content or null
 */
export const BlockReplacement: React.FC<BlockReplacementProps> = ({ 
  block
}) => {
  // Get replacements from context
  const { replacements: contextReplacements } = useBlockKit();
  
  const { ref, default: defaultContent } = block;
  
  
  // Check if we have a replacement for this ref
  let replacement = ref ? contextReplacements[ref] : null;
  
  // Helper function to render blocks with proper typing
  const renderBlocks = (blocks: BlockElement | BlockElement[]): React.ReactElement | null => {
    const blocksArray = Array.isArray(blocks) ? blocks : [blocks];
    if (blocksArray.length === 0) return null;
    
    return React.createElement(BlockRenderer, { 
      blocks: blocksArray,
      replacements: contextReplacements 
    });
  };

  // If we have a replacement, render it
  if (replacement && typeof replacement === 'object' && 'build' in replacement) {
    const builder = replacement as BlockBuilder;
    const builtReplacement = builder.build();
    return renderBlocks(builtReplacement);
  }
  
  // If we have a direct replacement, render it
  if (replacement) {
    return renderBlocks(replacement as BlockElement | BlockElement[]);
  }
  
  // If we have a default, render it
  if (defaultContent) {
    return renderBlocks(defaultContent);
  }
  
  // No replacement or default, render nothing
  return null;
};

export default BlockReplacement;
